import { Component } from '@theme/component';

const HOLD_MS = 250; // Longer presses pause without navigating

/**
 * @typedef {Object} SpotlightRefs
 * @property {HTMLElement[]} stories
 * @property {HTMLElement[]} fills
 * @property {HTMLElement} [edge]
 */

/**
 * Instagram-style stories: images run for their set duration, videos for their own length,
 * then the next story slides in. Loops back to the first.
 *
 * @extends {Component<SpotlightRefs>}
 */
class SpotlightComponent extends Component {
	requiredRefs = ['stories'];
	#index = 0;
	#elapsed = 0;
	#last = 0;
	#frame = 0;
	#pressAt = 0;
	/** Reasons the stories are paused: user (desktop click), hold, focus, offscreen, hidden, editor */
	#paused = new Set(['offscreen']);
	/** @type {AbortController | undefined} */
	#abort;
	/** @type {IntersectionObserver | undefined} */
	#observer;
	#desktop = window.matchMedia('(min-width: 750px) and (orientation: landscape)');
	/** @type {ResizeObserver | undefined} */
	#headerObserver;
	/** @type {ReturnType<typeof setTimeout> | undefined} */
	#viewTimer;
	/** Story instances already reported to telemetry on this page */
	#viewed = new Set();

	#measure = () => {
		const top = this.getBoundingClientRect().top + window.scrollY;
		this.style.setProperty('--spotlight-offset', `${Math.round(top)}px`);

		// Desktop side cards end on the header's content edge (logo / last icon). They keep the Figma
		// 0.82 scale when there's room and shrink, down to 0.5, when the active card is wide.
		const card = this.refs.stories[0]?.offsetWidth;
		const edge = this.refs.edge?.offsetWidth;
		if (!card || edge === undefined) return;

		const gap = card * 0.057; // Figma: 28px gap beside a 491px card
		const reach = this.offsetWidth / 2 - edge; // Centre to header edge
		const scale = Math.min(0.82, Math.max(0.5, (reach - card / 2 - gap) / card));
		// Gap to the active card is always the Figma gap: with spare room the side cards sit inside the
		// header line rather than drifting out to it
		const step = card / 2 + gap + (scale * card) / 2;
		this.style.setProperty('--spotlight-side-scale', scale.toFixed(3));
		this.style.setProperty('--spotlight-step', `${step}px`);
	};

	connectedCallback() {
		super.connectedCallback();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.addEventListener('pointerdown', this.#onPointerDown, { signal });
		this.addEventListener('pointerup', this.#onPointerUp, { signal });
		this.addEventListener('pointercancel', () => this.#resume('hold'), { signal });
		this.addEventListener('focusin', (e) => e.target instanceof Element && e.target.matches(':focus-visible') && this.#pause('focus'), { signal });
		this.addEventListener('focusout', (e) => !this.contains(/** @type {Node | null} */ (e.relatedTarget)) && this.#resume('focus'), { signal });
		this.addEventListener('keydown', this.#onKeyDown, { signal });

		for (const story of this.refs.stories) {
			story.querySelector('video')?.addEventListener('ended', () => story === this.#story && this.next(), { signal });
		}

		this.#desktop.addEventListener('change', () => this.#syncAmbient(), { signal });

		document.addEventListener('visibilitychange', () => (document.hidden ? this.#pause('hidden') : this.#resume('hidden')), { signal });
		document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
		document.addEventListener('shopify:block:deselect', () => this.#resume('editor'), { signal });

		this.#observer = new IntersectionObserver(
			([entry]) => (entry?.isIntersecting ? this.#resume('offscreen') : this.#pause('offscreen')),
			{ threshold: 0.25 }
		);
		this.#observer.observe(this);

		// Whatever sits above the section (header, announcement bar or neither) is subtracted from 100svh,
		// and the side cards are refitted whenever the section resizes
		this.#headerObserver = new ResizeObserver(this.#measure);
		this.#headerObserver.observe(this);
		const headerGroup = document.querySelector('#header-group');
		if (headerGroup) this.#headerObserver.observe(headerGroup);
		this.#measure();

		this.#go(0);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abort?.abort();
		this.#observer?.disconnect();
		this.#headerObserver?.disconnect();
		clearTimeout(this.#viewTimer);
		cancelAnimationFrame(this.#frame);
	}

	next() {
		this.#go(this.#index + 1);
	}

	previous() {
		this.#go(this.#index - 1);
	}

	get #story() {
		return this.refs.stories[this.#index];
	}

	get #video() {
		return this.#story?.querySelector('video');
	}

	/**
	 * Circular distance from the active story, clamped to the two visible slots each side.
	 * @param {number} i
	 * @param {number} previous - Breaks the even-count tie so a card keeps exiting the way it was heading
	 */
	#offset(i, previous) {
		const count = this.refs.stories.length;
		let offset = (((i - this.#index) % count) + count) % count;
		if (offset > count / 2 || (offset === count / 2 && previous <= 0)) offset -= count;
		return Math.max(-2, Math.min(2, offset));
	}

	/** @param {number} index */
	#go(index) {
		const { stories, fills } = this.refs;
		const wasActive = this.#story;
		this.#index = (index + stories.length) % stories.length;
		this.#elapsed = 0;

		stories.forEach((story, i) => {
			const previous = Number(story.style.getPropertyValue('--offset'));
			const offset = this.#offset(i, previous);

			// Cards wrapping round enter from the far edge instead of sweeping across the screen
			if (Math.abs(offset - previous) > 1) {
				story.style.transition = 'none';
				story.style.setProperty('--offset', String(Math.sign(offset) * 2));
				story.offsetWidth; // Flush so the jump isn't animated
				story.style.transition = '';
			}

			story.style.setProperty('--offset', String(offset));
			story.dataset.state = offset === 0 ? 'active' : Math.abs(offset) === 1 ? 'near' : 'far';
			story.setAttribute('aria-hidden', String(offset !== 0));

			fills[i]?.style.setProperty('--progress', i < this.#index ? '1' : '0');		});

		const video = this.#video;
		if (video && this.#story !== wasActive) {
			video.loop = false;
			video.defaultPlaybackRate = video.playbackRate = 1;
			video.currentTime = 0;
		}

		this.#syncAmbient();
		this.#play();
	}

	/** Desktop side cards play their video muted in slow motion until it's their turn */
	#syncAmbient() {
		const ambient = this.#desktop.matches && !this.#paused.has('offscreen') && !this.#paused.has('hidden');

		this.refs.stories.forEach((story) => {
			const video = story.querySelector('video');
			if (!video || story === this.#story) return;

			if (ambient && story.dataset.state === 'near') {
				video.loop = true;
				video.defaultPlaybackRate = video.playbackRate = 0.5;
				video.play().catch(() => {});
			} else {
				video.pause();
			}
		});
	}

	#play() {
		cancelAnimationFrame(this.#frame);
		const video = this.#video;

		if (this.#paused.size) {
			video?.pause();
			return;
		}

		if (video) {
			video.play().catch(() => {}); // Blocked autoplay (e.g. iOS Low Power) falls back to the duration timer
		}

		this.#last = performance.now();
		this.#frame = requestAnimationFrame(this.#tick);
		this.#trackView();
	}

	/**
	 * Telemetry impression for the active story once it has played for a second on screen, once per story per page.
	 * Same event and element key telemetry.js sends for other blocks; it skips stories so the dimmed ones don't count.
	 */
	#trackView() {
		clearTimeout(this.#viewTimer);
		const story = this.#story;
		if (!story || !(/** @type {any} */ (window).peTelemetry?.store_key)) return;

		this.#viewTimer = setTimeout(() => {
			if (this.#paused.size || story !== this.#story) return;
			const raw = story.dataset.peEl ?? '';
			const cut = raw.lastIndexOf(':');
			const instance = raw.slice(cut + 1).split('__').pop()?.replace(/-\d+$/, '') ?? '';
			if (this.#viewed.has(instance)) return;
			this.#viewed.add(instance);
			try {
				/** @type {any} */ (window).Shopify.analytics.publish('pe:impression', {
					element_type: raw.slice(0, cut),
					element_instance: instance,
				});
			} catch (error) {
				/* analytics must never break the page */
			}
		}, 1000);
	}

	/** @param {number} now */
	#tick = (now) => {
		const video = this.#video;
		let progress;

		if (video && !video.paused) {
			progress = video.duration ? video.currentTime / video.duration : 0;
		} else {
			this.#elapsed += now - this.#last;
			progress = this.#elapsed / (Number(this.#story?.dataset.duration || 5) * 1000);
		}

		this.#last = now;
		this.refs.fills[this.#index]?.style.setProperty('--progress', String(Math.min(progress, 1)));

		if (progress >= 1 && !video?.ended) return this.next();
		this.#frame = requestAnimationFrame(this.#tick);
	};

	/** @param {string} reason */
	#pause(reason) {
		this.#paused.add(reason);
		cancelAnimationFrame(this.#frame);
		this.#video?.pause();
		this.#syncAmbient();
	}

	/** @param {string} reason */
	#resume(reason) {
		if (!this.#paused.delete(reason)) return;
		this.#syncAmbient();
		if (!this.#paused.size) this.#play();
	}

	/** @param {PointerEvent} event */
	#onPointerDown = (event) => {
		if (event.button !== 0 || !(event.target instanceof Element) || event.target.closest('a, button')) return;
		this.#pressAt = event.timeStamp;
		this.#pause('hold');
	};

	/** @param {PointerEvent} event */
	#onPointerUp = (event) => {
		if (!this.#pressAt) return;
		const held = event.timeStamp - this.#pressAt > HOLD_MS;
		this.#pressAt = 0;
		this.#resume('hold');
		if (held || !(event.target instanceof Element)) return;

		const story = /** @type {HTMLElement | null} */ (event.target.closest('.spotlight-story'));
		const index = story ? this.refs.stories.indexOf(story) : -1;
		if (!story || index === -1) return;
		if (index !== this.#index) return this.#go(index);
		// Desktop, like Instagram's: clicking the active video pauses/resumes; side cards and arrow keys move between stories
		if (this.#desktop.matches) return this.#togglePlayback();

		// Instagram rule: left third goes back, the rest goes forward
		const { left, width } = story.getBoundingClientRect();
		if (event.clientX - left < width / 3) this.previous();
		else this.next();
	};

	/** @param {KeyboardEvent} event */
	#onKeyDown = (event) => {
		if (event.key === 'ArrowRight') this.next();
		if (event.key === 'ArrowLeft') this.previous();
	};

	#togglePlayback = () => {
		if (!this.#paused.has('user')) this.#pause('user');
		else this.#resume('user');
	};

	/** @param {Event} event */
	#onBlockSelect = (event) => {
		const index = this.refs.stories.findIndex((story) => story.contains(/** @type {Node} */ (event.target)));
		if (index === -1) return;
		this.#pause('editor');
		this.#go(index);
	};
}

if (!customElements.get('spotlight-component')) {
	customElements.define('spotlight-component', SpotlightComponent);
}
